import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getPOSTerminals, createPOSTerminal, updatePOSTerminal, getBranches } from '../../services/api';
import { Plus, Copy, Edit, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const CajasPOS = () => {
  const { user } = useAuth();
  const [terminals, setTerminals] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    branch_id: '',
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.business_id) return;

    try {
      const branchesRes = await getBranches(user.business_id);
      setBranches(branchesRes.data);
      
      if (branchesRes.data.length > 0) {
        setSelectedBranch(branchesRes.data[0].id);
        loadTerminals(branchesRes.data[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadTerminals = async (branchId) => {
    try {
      const response = await getPOSTerminals(branchId);
      setTerminals(response.data);
    } catch (error) {
      console.error('Error loading terminals:', error);
      toast.error('Error al cargar cajas POS');
    }
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
    loadTerminals(branchId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const terminalData = {
        ...formData,
        branch_id: selectedBranch,
      };

      await createPOSTerminal(terminalData);
      toast.success('Caja POS creada exitosamente');
      setShowModal(false);
      setFormData({ name: '', branch_id: '' });
      loadTerminals(selectedBranch);
    } catch (error) {
      console.error('Error creating terminal:', error);
      toast.error(getErrorMessage(error, 'Error al crear caja POS'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Hash copiado al portapapeles');
  };

  const toggleTerminalStatus = async (terminalId, currentStatus) => {
    try {
      await updatePOSTerminal(terminalId, { is_active: !currentStatus });
      toast.success(`Caja ${!currentStatus ? 'activada' : 'desactivada'}`);
      loadTerminals(selectedBranch);
    } catch (error) {
      console.error('Error updating terminal:', error);
      toast.error('Error al actualizar caja');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Cajas POS</h1>
            <p className="text-slate-600 mt-2">Gestiona los puntos de venta</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-terminal-button"
            className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nueva Caja POS
          </button>
        </div>

        {/* Branch Selector */}
        <div className="bg-white p-4 rounded-lg border border-slate-200">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Selecciona Sucursal
          </label>
          <select
            value={selectedBranch}
            onChange={(e) => handleBranchChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        {/* Terminals List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {terminals.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
              <p className="text-slate-500">No hay cajas POS registradas para esta sucursal</p>
            </div>
          ) : (
            terminals.map((terminal) => (
              <div
                key={terminal.id}
                data-testid={`terminal-card-${terminal.id}`}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{terminal.name}</h3>
                    <p className="text-sm text-slate-500">
                      {terminal.is_active ? (
                        <span className="text-green-600 font-medium">Activa</span>
                      ) : (
                        <span className="text-red-600 font-medium">Inactiva</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleTerminalStatus(terminal.id, terminal.is_active)}
                    className={`p-2 rounded-lg ${
                      terminal.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                    }`}
                  >
                    {terminal.is_active ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                  </button>
                </div>

                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-slate-500 font-medium mb-1">Hash de Instalación</p>
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                      <code className="text-xs font-mono text-slate-700 flex-1 truncate">
                        {terminal.terminal_hash}
                      </code>
                      <button
                        onClick={() => copyToClipboard(terminal.terminal_hash)}
                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                        title="Copiar hash"
                      >
                        <Copy className="w-4 h-4 text-slate-600" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-slate-500">
                    <p>ID: {terminal.id.slice(0, 8)}...</p>
                    <p>Creada: {new Date(terminal.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    Usa este hash para configurar la terminal POS
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Nueva Caja POS</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nombre de la Caja *
                </label>
                <input
                  type="text"
                  required
                  data-testid="terminal-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Ej: Caja 1, Caja Principal"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Se generará automáticamente un hash único para esta caja POS que deberás usar al instalar el sistema.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  data-testid="terminal-form-submit"
                  className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                >
                  Crear Caja POS
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CajasPOS;
